






























































































































import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import BreadcrumbComponent from '@/_shared/components/layout/breadcrumb.component.vue';
import RankAdvancementService from '@/achieve/services/rank-advancement/rank-advancement.service';
import AdvancementParams from '@/achieve/interfaces/advancement-params.interface';
import moment from 'moment';
import RankAdvancementEntity from '@/achieve/entities/rank-advancement/rank-advancement.entity';
import RankAdvancementLocation from '@/achieve/entities/rank-advancement/rank-advancement-location.entity';
import CountryRankAdvancement from '@/achieve/entities/rank-advancement/country-rank-advancement.entity';
import RankAdvancementRegion from '@/achieve/entities/rank-advancement/rank-advancement-region.entity';
import { getRankById, getValidRanks } from '@/_shared/constants/ranks';
import Rank from '@/_shared/entities/rank/rank.entity';
import RankAdvancementCard from '@/achieve/components/rank-advancement/rank-advancement-card.component.vue';
import NoResults from '@/_shared/components/globals/no-results.component.vue';
import RepAdvancementEntity from '@/achieve/entities/rank-advancement/rep-advancement.entity';
import months from '@/_shared/constants/months';
import FrontendPagination from '@/_shared/components/pagination/frontend-pagination.component.vue';
import RankBar from '@/_shared/components/globals/rank-bar.component.vue';
import cloneDeep from 'lodash/cloneDeep';

@Component({
  components: {
    FrontendPagination,
    BreadcrumbComponent,
    RankAdvancementCard,
    NoResults,
    RankBar,
  },
})
export default class RankAdvancementView extends Vue {
  readonly currentDate = moment().subtract(1, 'month').startOf('month');
  selectedLocation: RankAdvancementRegion | null = null;
  selectedRank: Rank | null = null;
  selectedDate = new Date(this.currentDate.toISOString());
  allRanks = getValidRanks();
  advancement: RankAdvancementEntity | null = null;
  locations: RankAdvancementLocation | null = null;
  highestRanks: CountryRankAdvancement[] = [];
  currentPageItems: RepAdvancementEntity[] = [];
  itemsPerPage = 24;
  currentPage = 1;

  get dateLabel(): string {
    const momentDate = moment(this.selectedDate);
    const month = momentDate.month();
    const year = momentDate.year();
    const monthLabel = months.find((monthConstant) => monthConstant.id === month)?.key;
    if (!monthLabel) return '';
    return `${this.$t(monthLabel)}, ${year}`;
  }

  get showPreviousButton(): boolean {
    const selectedDate = moment(this.selectedDate);
    const minimumDate = moment(this.currentDate).subtract(1, 'year');
    return selectedDate.isAfter(minimumDate);
  }

  get showNextButton(): boolean {
    const selectedDate = moment(this.selectedDate);
    return selectedDate.isBefore(this.currentDate);
  }

  get advancements(): RepAdvancementEntity[] {
    return this.advancement?.advancement || [];
  }

  get allLocations(): RankAdvancementRegion[] {
    if (!this.locations) return [];
    const { regions, countries } = cloneDeep(this.locations);
    countries.sort((a, b) => a.description.localeCompare(b.description));
    return regions.concat(countries);
  }

  get showNoDataPublishedMessage(): boolean {
    const selectedDate = moment(this.selectedDate);
    const isMostRecentMonth = selectedDate.isSame(this.currentDate);
    return !this.advancements.length && isMostRecentMonth;
  }

  selectLocation(location: RankAdvancementRegion): void {
    this.$store.dispatch('showLoading');
    this.selectedLocation = location;
    this.setHighestRank();
    this.currentPage = 1;
    this.getAdvancement().finally(() => {
      this.$store.dispatch('hideLoading');
    });
  }

  onUpdatePage(): void {
    this.scrollToTop();
  }

  scrollToTop() {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }, 50);
  }

  goToPreviousMonth(): void {
    const selectedDate = moment(this.selectedDate);
    const modifiedDate = selectedDate.subtract(1, 'month');
    this.selectedDate = new Date(modifiedDate.toISOString());
    this.handleChangeMonth();
  }

  goToNextMonth(): void {
    const selectedDate = moment(this.selectedDate);
    const modifiedDate = selectedDate.add(1, 'month');
    this.selectedDate = new Date(modifiedDate.toISOString());
    this.handleChangeMonth();
  }

  handleChangeMonth(): void {
    this.$store.dispatch('showLoading');
    this.getHighestRanks().then(() => {
      this.setHighestRank();
      this.getAdvancement().finally(() => {
        this.scrollToTop();
        this.$store.dispatch('hideLoading');
      });
    });
  }

  handleChangeRank(): void {
    this.$store.dispatch('showLoading');
    this.currentPage = 1;
    this.getAdvancement().finally(() => {
      this.scrollToTop();
      this.$store.dispatch('hideLoading');
    });
  }

  getAdvancement(): Promise<void> {
    const params: AdvancementParams = {
      regionId: this.selectedLocation?.id || 0,
      rankId: this.selectedRank?.id || 0,
      month: moment(this.selectedDate).format('YYMM'),
      page: 1,
    };
    return RankAdvancementService.getAdvancement(params).then((response) => {
      this.advancement = response;
      this.sortAdvancements();
    }).catch(() => {
      this.advancement = new RankAdvancementEntity();
    });
  }

  sortAdvancements(): void {
    if (!this.advancement?.advancement) return;
    this.advancement.advancement.sort(
      (a, b) => a.name.localeCompare(b.name),
    );
  }

  getLocations(): Promise<void> {
    return RankAdvancementService.getLocations().then((response) => {
      this.locations = response;
    });
  }

  getHighestRanks(): Promise<void> {
    const selectedDate = moment(this.selectedDate);
    return RankAdvancementService.getHighestRankAdvancements(
      selectedDate.startOf('month').format('YYYY-MM-DD'),
      selectedDate.endOf('month').format('YYYY-MM-DD'),
    ).then((response) => {
      this.highestRanks = response;
    });
  }

  retrieveData() {
    this.$store.dispatch('showLoading');
    return Promise.all([
      this.getLocations(),
      this.getHighestRanks(),
    ]).then(async () => {
      this.setDefaultRegion();
      this.setHighestRank();
      await this.getAdvancement();
    }).finally(() => {
      this.$store.dispatch('hideLoading');
    });
  }

  setHighestRank(): void {
    if (!this.selectedLocation?.id) {
      this.setHighestRankFromAllCountries();
      return;
    }
    this.setHighestRanksFromCountry();
  }

  setHighestRanksFromCountry() {
    const highestRankFromRegion = this.highestRanks
      .find((rank) => rank.country === this.selectedLocation?.name);
    if (!highestRankFromRegion?.rankTypeId) return;
    this.selectedRank = getRankById(highestRankFromRegion.rankTypeId) || null;
  }

  setHighestRankFromAllCountries() {
    const allRanks = this.highestRanks.map((country) => country.rankTypeId);
    const highestRank = Math.max(...allRanks);
    this.selectedRank = getRankById(highestRank) || null;
  }

  setDefaultRegion(): void {
    if (!this.locations) return;
    const defaultRegion = this.locations.regions[0];
    const { countries } = this.locations || {};
    const userCountry = this.$store.getters.userShippingCountry;
    const repRegion = countries.find((country) => country.id === userCountry?.id);
    this.selectedLocation = repRegion || defaultRegion;
  }

  created() {
    this.retrieveData();
  }
}
